import "../styling/scss/App.scss";
import { useEffect, useState, useContext, useMemo } from "react";
import { Row, Col, Tab, Tabs, Container, Form } from "react-bootstrap";
import { SXButton } from "@common/components/ui-components/SXButton";
import { faCopy } from "@fortawesome/free-solid-svg-icons";

import { ng } from "@common/ngds";
import {
  getLabel,
  getScaleFactor,
  getSteelControlTypeName,
  getConcreteControlTypeName,
  getTimberControlTypeName,
  FakeEvent,
} from "../helpers/auxiliary";
import { getLoadcomb } from "@common/helpers/protoGen";
import { useAppSelector, useAppDispatch } from "../app/hooks";
import { interpolate, isSLS, isULS, SymbolName } from "@common/helpers/funlib";
import { activeLCOs, selectProject } from "../features/projects/projectSlice";

import {
  IStageDictionary,
  IReactionForceDictionary,
  ISectionForceDictionary,
  IDisplacementDictionary,
  IDesignResultDictionary,
} from "@common/types/sharedTypes";

// --- Interfaces ---
interface NGResultTablesProps {
  selectedLcomb: string;
  results: any;
  units: any;
  user: any;
  isUlsComb?: boolean;
  isSlsComb?: boolean;
  codeCheckResultExists?: boolean;
  elementResultsForces: ISectionForceDictionary;
  supportForces: IReactionForceDictionary;
  displacement: IDisplacementDictionary;
  designResult: IDesignResultDictionary;
  loadComboHandle: (e: React.ChangeEvent<HTMLSelectElement> | FakeEvent, dropLCOs: ng.loadcombination.Data[]) => void;
  setGraphSymbol: (newSymbol: SymbolName, remove: SymbolName[]) => void;
}

// --- Main component ---
export const SXResultTables = (props: NGResultTablesProps) => {
  // --- Contexts --
  // --- Local variables ---
  const aproject = useAppSelector(selectProject);
  const dropLCOs = useAppSelector(activeLCOs);

  const lang = "en";
  let localisation = {};

  const [showCopyMessage, setShowCopyMessage] = useState(false);
  const [isFirefox, setIsFirefox] = useState(false);
  const [selLCO, setSelLCO] = useState<string>("-1");

  useEffect(() => {
    const detectBrowser = () => {
      const isFirefoxBrowser = /Firefox/i.test(navigator.userAgent);
      setIsFirefox(isFirefoxBrowser);
    };
    detectBrowser();
  }, []);

  /** copyToClipboard utilizes the css class
   * .copy-result-table to function and especially the property 'user-select'.
   * That class is found in App.scss
   * NOTE!!! IF more tables are to be added then the $tables variable
   * in .copy-result-table in App.scss needs to be updated accordingly for it to function,
   * and the newly generated classNames needs to be added to the tables in
   * return statement below.
   *
   * Doesn't seem to work on FireFox so buttons are hidden by setIsFirefox.
   */
  const copyToClipboard = async (tableClassName: string) => {
    try {
      const table = document.querySelector(`.${tableClassName}`);
      if (table) {
        const clipboardItem = new ClipboardItem({
          "text/plain": new Blob([table.innerHTML], { type: "text/plain" }),
          "text/html": new Blob([table.outerHTML], { type: "text/html" }),
        });
        navigator.clipboard.write([clipboardItem]);
        setShowCopyMessage(true);
        setTimeout(() => {
          setShowCopyMessage(false);
        }, 1500);
      }
    } catch (error) {
      console.error("Failed to copy table to clipboard:", error);
    }
  };
  /*
    console.log("isUlsComb : ", isUlsComb);
    console.log("isSlsComb : ", isSlsComb);
    console.log("codeCheckResultExists : ", codeCheckResultExists);
    console.log("selLCO : ", selLCO);
  */
  return (
    <>
      <Row style={{ alignItems: "center" }}>
        <Col xs={"auto"} style={{}}>
          loadcomb_
        </Col>
        <Col xs={"auto"}>
          <Form.Select
            name="project_result_load_comb"
            value={selLCO}
            //className="pointer loadcombo-select"
            className="modal-btn"
            size={"sm"}
            style={{ width: "100%" }}
            disabled={false}
            onChange={(e) => {
              setSelLCO(e.target.value.replace("envelope_", ""));
            }}
          >
            <option value={-1} defaultValue={-1} style={{ color: "#3c74a8" }}>
              none_
            </option>
            {dropLCOs?.map((lco: ng.loadcombination.IData, index: number) => {
              if (!lco.deactive) {
                if (lco.id) {
                  return (
                    <option key={index + 1} value={lco.id.guid || ""}>
                      {lco.id.name}
                    </option>
                  );
                }
              }
              return <></>;
            })}
          </Form.Select>
        </Col>
      </Row>

      <br />

      <Row>
        <Col>
          <Tabs id="ngtabs">
            {/** Element Results */}
            <Tab eventKey="0" title={"element_result_"}>
              <Row className="d-flex align-items-center justify-content-end" style={{ marginBottom: 5 }}>
                <Col xs={8}></Col>
                <Col xs={2} className={`text-center copy-message ${showCopyMessage ? "show" : ""}`}>
                  <span>{"table_copied_"}</span>
                </Col>
                <Col xs={2}>
                  {!isFirefox && (
                    <SXButton
                      button
                      size="sm"
                      className="modal-btn"
                      style={{ float: "right" }}
                      title="Copy table"
                      icon={faCopy}
                      onClick={() => copyToClipboard("copy-result-table-0")}
                    >
                      {"copy_project_"}
                    </SXButton>
                  )}
                </Col>
              </Row>
              <Row>
                <Container id="ngtable" className="copy-result-table-0">
                  {/* HEADING */}
                  <table className="table">
                    <thead>
                      <tr className="table-th-header align-items-center">
                        <th className="text-center table-header-bg">
                          position_ {props.units.getDisplayHeader("length_") ?? ""}
                        </th>
                        <th className="text-center table-header-bg">
                          N {props.units.getDisplayHeader("force_") ?? ""}
                        </th>
                        <th className="text-center table-header-bg">
                          V {props.units.getDisplayHeader("force_") ?? ""}
                        </th>
                        {props.isUlsComb && selLCO !== "envelope" && props.codeCheckResultExists && (
                          <th className="text-center table-header-bg">
                            V' {props.units.getDisplayHeader("force_") ?? ""}
                          </th>
                        )}
                        <th className="text-center table-header-bg">
                          M {props.units.getDisplayHeader("moment_") ?? ""}
                        </th>
                      </tr>
                    </thead>
                    {/* TABLE */}
                    <tbody>
                      {props.elementResultsForces[selLCO] &&
                        selLCO !== "" &&
                        Object.keys(props.elementResultsForces[selLCO]).map((key, index) => {
                          return props.elementResultsForces[selLCO][key].map((row, n) => {
                            let shearXS = 6;
                            if (Math.abs(row.V2.max) < 1e-3 && row.V2.min < 0) {
                              shearXS = 0;
                            }
                            if (
                              (Number(row.V2.max) > 0 && Math.abs(row.V2.min) < 1e-3) ||
                              (Math.abs(row.V2.max) < 1e-3 && Math.abs(row.V2.min) < 1e-3) ||
                              selLCO !== "envelope"
                            ) {
                              shearXS = 12;
                            }

                            let normXS = 6;
                            if (Math.abs(row.N.max) < 1e-3 && row.N.min < 0) {
                              normXS = 0;
                            }
                            if (
                              (Number(row.N.max) > 0 && Math.abs(row.N.min) < 1e-3) ||
                              (Math.abs(row.N.max) < 1e-3 && Math.abs(row.N.min) < 1e-3) ||
                              selLCO !== "envelope"
                            ) {
                              normXS = 12;
                            }

                            let momXS = 6;
                            if (Math.abs(row.M1.max) < 1e-3 && row.M1.min < 0) {
                              momXS = 0;
                            }
                            if (
                              (Number(row.M1.max) > 0 && Math.abs(row.M1.min) < 1e-3) ||
                              (Math.abs(row.M1.max) < 1e-3 && Math.abs(row.M1.min) < 1e-3) ||
                              selLCO !== "envelope"
                            ) {
                              momXS = 12;
                            }

                            return (
                              <tr className="table-body justify-content-center align-items-center" key={n}>
                                <td id={`tab${0}-row${n}-col${0}`} className="text-center no-border">
                                  {(
                                    row.pos *
                                    getScaleFactor({ db: localisation, tag: "length_", settings: props.user.settings })
                                  ).toFixed(3)}
                                </td>

                                <td className="text-center no-border">
                                  <div className="d-flex justify-content-between">
                                    {normXS !== 0 && (
                                      <div
                                        id={`tab${0}-row${n}-col${1}`}
                                        style={{ flexBasis: `${(normXS * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.N.max *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "force_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                    {normXS !== 12 && (
                                      <div
                                        id={`tab${0}-row${n}-col${1}`}
                                        style={{ flexBasis: `${((12 - normXS) * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.N.min *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "force_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </td>

                                <td className="text-center no-border">
                                  <div className="d-flex justify-content-between">
                                    {shearXS !== 0 && (
                                      <div
                                        id={`tab${0}-row${n}-col${2}`}
                                        style={{ flexBasis: `${(shearXS * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.V2.max *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "force_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                    {shearXS !== 12 && (
                                      <div
                                        id={`tab${0}-row${n}-col${2}`}
                                        style={{ flexBasis: `${((12 - shearXS) * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.V2.min *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "force_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </td>

                                {props.isUlsComb && selLCO !== "envelope" && props.codeCheckResultExists && (
                                  <td className="text-center no-border">
                                    <div className="d-flex justify-content-between">
                                      {shearXS !== 0 && (
                                        <div
                                          id={`tab${0}-row${n}-col${2}`}
                                          style={{ flexBasis: `${(shearXS * 100) / 12}%` }}
                                          className="text-center no-border"
                                        >
                                          {(
                                            row.V1.max *
                                            getScaleFactor({
                                              db: localisation,
                                              tag: "force_",
                                              settings: props.user.settings,
                                            })
                                          ).toFixed(2)}
                                        </div>
                                      )}
                                      {shearXS !== 12 && (
                                        <div
                                          id={`tab${0}-row${n}-col${2}`}
                                          style={{ flexBasis: `${((12 - shearXS) * 100) / 12}%` }}
                                          className="text-center no-border"
                                        >
                                          {(
                                            row.V1.min *
                                            getScaleFactor({
                                              db: localisation,
                                              tag: "force_",
                                              settings: props.user.settings,
                                            })
                                          ).toFixed(2)}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                )}

                                <td className="text-center no-border">
                                  <div className="d-flex justify-content-between">
                                    {momXS !== 0 && (
                                      <div
                                        id={`tab${0}-row${n}-col${3}`}
                                        style={{ flexBasis: `${(momXS * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.M1.max *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "moment_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                    {momXS !== 12 && (
                                      <div
                                        id={`tab${0}-row${n}-col${3}`}
                                        style={{ flexBasis: `${((12 - momXS) * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.M1.min *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "moment_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })}
                    </tbody>
                  </table>
                </Container>
              </Row>
            </Tab>

            {/** Support Results */}
            <Tab eventKey="1" title={getLabel(localisation, lang, "support_result_", true)}>
              <Row className="d-flex align-items-center justify-content-end" style={{ marginBottom: 5 }}>
                <Col xs={8}></Col>
                <Col xs={2} className={`text-center copy-message ${showCopyMessage ? "show" : ""}`}>
                  <span>{getLabel(localisation, lang, "table_copied_")}</span>
                </Col>
                <Col xs={2}>
                  {!isFirefox && (
                    <SXButton
                      button
                      size="sm"
                      className="modal-btn"
                      style={{ float: "right" }}
                      title="Copy table"
                      icon={faCopy}
                      onClick={() => copyToClipboard("copy-result-table-1")}
                    >
                      {getLabel(localisation, lang, "copy_project_")}
                    </SXButton>
                  )}
                </Col>
              </Row>
              <Row>
                <Container id="ngtable" className="copy-result-table-1">
                  {/* HEADING */}
                  <table className="table">
                    <thead>
                      <tr className="table-th-header align-items-center">
                        <th className="text-center table-header-bg">
                          {getLabel(localisation, lang, "position_")} {props.units.getDisplayHeader("length_")}
                        </th>
                        <th className="text-center table-header-bg">
                          {getLabel(localisation, lang, "reactionforce_vertical_", true)}
                          <sub>z</sub> {props.units.getDisplayHeader("reactionforce_vertical_")}
                        </th>
                        <th className="text-center table-header-bg">
                          {getLabel(localisation, lang, "reactionforce_horizontal_", true)}
                          <sub>x</sub> {props.units.getDisplayHeader("reactionforce_horizontal_")}
                        </th>
                        <th className="text-center table-header-bg">
                          {getLabel(localisation, lang, "reactionmoment_", true)}
                          <sub>R</sub> {props.units.getDisplayHeader("reactionmoment_")}
                        </th>
                        <th className="text-center table-header-bg">
                          {getLabel(localisation, lang, "reaction_torque_", true)}
                          <sub>R</sub> {props.units.getDisplayHeader("reaction_torque_")}
                        </th>
                      </tr>
                    </thead>

                    {/* TABLE */}
                    <tbody>
                      {props.supportForces[selLCO] &&
                        selLCO !== "" &&
                        Object.keys(props.supportForces[selLCO]).map((key, index) => {
                          return props.supportForces[selLCO][key].map((row, n) => {
                            let vertXS = 6;
                            if (Math.abs(row.Rz.max) < 1e-3 && row.Rz.min < 0) {
                              vertXS = 0;
                            }
                            if (
                              (Number(row.Rz.max) > 0 && Math.abs(row.Rz.min) < 1e-3) ||
                              (Math.abs(row.Rz.max) < 1e-3 && Math.abs(row.Rz.min) < 1e-3) ||
                              selLCO !== "envelope"
                            ) {
                              vertXS = 12;
                            }

                            let horXS = 6;
                            if (Math.abs(row.Rx.max) < 1e-3 && row.Rx.min < 0) {
                              horXS = 0;
                            }
                            if (
                              (Number(row.Rx.max) > 0 && Math.abs(row.Rx.min) < 1e-3) ||
                              (Math.abs(row.Rx.max) < 1e-3 && Math.abs(row.Rx.min) < 1e-3) ||
                              selLCO !== "envelope"
                            ) {
                              horXS = 12;
                            }

                            let momXS = 6;
                            if (Math.abs(row.My.max) < 1e-3 && row.My.min < 0) {
                              momXS = 0;
                            }
                            if (
                              (Number(row.My.max) > 0 && Math.abs(row.My.min) < 1e-3) ||
                              (Math.abs(row.My.max) < 1e-3 && Math.abs(row.My.min) < 1e-3) ||
                              selLCO !== "envelope"
                            ) {
                              momXS = 12;
                            }

                            let torqXS = 6;
                            if (Math.abs(row.Mx.max) < 1e-3 && row.Mx.min < 0) {
                              torqXS = 0;
                            }
                            if (
                              (Number(row.Mx.max) > 0 && Math.abs(row.Mx.min) < 1e-3) ||
                              (Math.abs(row.Mx.max) < 1e-3 && Math.abs(row.Mx.min) < 1e-3) ||
                              selLCO !== "envelope"
                            ) {
                              torqXS = 12;
                            }

                            return (
                              <tr className="table-body justify-content-center align-items-center ngtable" key={n}>
                                <td id={`tab${1}-row${n}-col${0}`} className="text-center no-border">
                                  {(
                                    row.pos *
                                    getScaleFactor({ db: localisation, tag: "length_", settings: props.user.settings })
                                  ).toFixed(3)}
                                </td>
                                <td className="text-center no-border">
                                  <div className="d-flex justify-content-between">
                                    {vertXS !== 0 && (
                                      <div
                                        id={`tab${1}-row${n}-col${1}`}
                                        style={{ flexBasis: `${(vertXS * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.Rz.max *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "force_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                    {vertXS !== 12 && (
                                      <div
                                        id={`tab${1}-row${n}-col${1}`}
                                        style={{ flexBasis: `${((12 - vertXS) * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.Rz.min *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "force_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="text-center no-border">
                                  <div className="d-flex justify-content-between">
                                    {horXS !== 0 && (
                                      <div
                                        id={`tab${1}-row${n}-col${2}`}
                                        style={{ flexBasis: `${(horXS * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.Rx.max *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "force_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                    {horXS !== 12 && (
                                      <div
                                        id={`tab${1}-row${n}-col${2}`}
                                        style={{ flexBasis: `${((12 - horXS) * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.Rx.min *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "force_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="text-center no-border">
                                  <div className="d-flex justify-content-between">
                                    {momXS !== 0 && (
                                      <div
                                        id={`tab${1}-row${n}-col${3}`}
                                        style={{ flexBasis: `${(momXS * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.My.max *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "moment_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                    {momXS !== 12 && (
                                      <div
                                        id={`tab${1}-row${n}-col${3}`}
                                        style={{ flexBasis: `${((12 - momXS) * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.My.min *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "moment_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="text-center no-border">
                                  <div className="d-flex justify-content-between">
                                    {torqXS !== 0 && (
                                      <div
                                        id={`tab${1}-row${n}-col${4}`}
                                        style={{ flexBasis: `${(torqXS * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.Mx.max *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "moment_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                    {torqXS !== 12 && (
                                      <div
                                        id={`tab${1}-row${n}-col${4}`}
                                        style={{ flexBasis: `${((12 - torqXS) * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.Mx.min *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "moment_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })}
                    </tbody>
                  </table>
                </Container>
              </Row>
            </Tab>

            {/** Displacement Results */}
            <Tab eventKey="2" title={getLabel(localisation, lang, "displacement_result_", true)}>
              <Row className="d-flex align-items-center justify-content-end" style={{ marginBottom: 5 }}>
                <Col xs={8}></Col>
                <Col xs={2} className={`text-center copy-message ${showCopyMessage ? "show" : ""}`}>
                  <span>{getLabel(localisation, lang, "table_copied_")}</span>
                </Col>
                <Col xs={2}>
                  {!isFirefox && (
                    <SXButton
                      button
                      size="sm"
                      className="modal-btn"
                      style={{ float: "right" }}
                      title="Copy table"
                      icon={faCopy}
                      onClick={() => copyToClipboard("copy-result-table-2")}
                    >
                      {getLabel(localisation, lang, "copy_project_")}
                    </SXButton>
                  )}
                </Col>
              </Row>
              <Row>
                <Container id="ngtable" className="copy-result-table-2">
                  <table style={{ tableLayout: "fixed", width: "100%" }} className="table">
                    {/* HEADING */}
                    <thead>
                      <tr className="table-th-header align-items-center">
                        <th className="text-center table-header-bg">
                          {" "}
                          {getLabel(localisation, lang, "position_", true)} {props.units.getDisplayHeader("length_")}
                        </th>
                        <th className="text-center table-header-bg">
                          {" "}
                          {getLabel(localisation, lang, "displacement_horizontal_", true)}
                          <sub>x</sub> {props.units.getDisplayHeader("displacement_horizontal_")}
                        </th>
                        <th className="text-center table-header-bg">
                          {" "}
                          {getLabel(localisation, lang, "displacement_vertical_", true)}
                          <sub>z</sub> {props.units.getDisplayHeader("displacement_vertical_")}
                        </th>
                        {props.isSlsComb && props.codeCheckResultExists && (
                          <th className="text-center table-header-bg">
                            {" "}
                            {getLabel(localisation, lang, "crack_width_", true)}{" "}
                            {props.units.getDisplayHeader("crack_width_")}
                          </th>
                        )}
                      </tr>
                    </thead>

                    {/* TABLE */}
                    <tbody>
                      {props.displacement[selLCO] &&
                        selLCO !== "" &&
                        Object.keys(props.displacement[selLCO]).map((key, index) => {
                          return props.displacement[selLCO][key].map((row, n) => {
                            let uXS = 6;
                            if (Math.abs(row.u.max) < 1e-7 && row.u.min < 0) {
                              uXS = 0;
                            }
                            if (
                              (Number(row.u.max) > 0 && Math.abs(row.u.min) < 1e-7) ||
                              (Math.abs(row.u.max) < 1e-7 && Math.abs(row.u.min) < 1e-7) ||
                              selLCO !== "envelope"
                            ) {
                              uXS = 12;
                            }

                            let vXS = 6;
                            if (Math.abs(row.v.max) < 1e-7 && row.v.min < 0) {
                              vXS = 0;
                            }
                            if (
                              (Number(row.v.max) > 0 && Math.abs(row.v.min) < 1e-7) ||
                              (Math.abs(row.v.max) < 1e-7 && Math.abs(row.v.min) < 1e-7) ||
                              selLCO !== "envelope"
                            ) {
                              vXS = 12;
                            }

                            let wXS = 6;
                            if (Math.abs(row.w.max) < 1e-7 && row.w.min < 0) {
                              wXS = 0;
                            }
                            if (
                              (Number(row.w.max) > 0 && Math.abs(row.w.min) < 1e-7) ||
                              (Math.abs(row.w.max) < 1e-7 && Math.abs(row.w.min) < 1e-7) ||
                              selLCO !== "envelope"
                            ) {
                              wXS = 12;
                            }

                            return (
                              <tr className="table-body justify-content-center align-items-center ngtable" key={n}>
                                <td id={`tab${2}-row${n}-col${0}`} className="text-center no-border">
                                  {(
                                    row.pos *
                                    getScaleFactor({ db: localisation, tag: "length_", settings: props.user.settings })
                                  ).toFixed(3)}
                                </td>

                                <td className="text-center no-border">
                                  <div className="d-flex justify-content-between">
                                    {uXS !== 0 && (
                                      <div
                                        id={`tab${2}-row${n}-col${1}`}
                                        style={{ flexBasis: `${(uXS * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.u.max *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "displacement_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(4)}
                                      </div>
                                    )}
                                    {uXS !== 12 && (
                                      <div
                                        id={`tab${2}-row${n}-col${1}`}
                                        style={{ flexBasis: `${((12 - uXS) * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.u.min *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "displacement_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(4)}
                                      </div>
                                    )}
                                  </div>
                                </td>

                                <td className="text-center no-border">
                                  <div className="d-flex justify-content-between">
                                    {wXS !== 0 && (
                                      <Col
                                        id={`tab${2}-row${n}-col${2}`}
                                        style={{ flexBasis: `${(wXS * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.w.max *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "displacement_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(4)}
                                      </Col>
                                    )}
                                    {wXS !== 12 && (
                                      <Col
                                        id={`tab${2}-row${n}-col${2}`}
                                        style={{ flexBasis: `${((12 - wXS) * 100) / 12}%` }}
                                        className="text-center no-border"
                                      >
                                        {(
                                          row.w.min *
                                          getScaleFactor({
                                            db: localisation,
                                            tag: "displacement_",
                                            settings: props.user.settings,
                                          })
                                        ).toFixed(4)}
                                      </Col>
                                    )}
                                  </div>
                                </td>

                                {props.isSlsComb && props.codeCheckResultExists && (
                                  <td className="text-center no-border">
                                    <div className="d-flex justify-content-between">
                                      {vXS !== 0 && (
                                        <Col
                                          id={`tab${2}-row${n}-col${2}`}
                                          style={{ flexBasis: `${(vXS * 100) / 12}%` }}
                                          className="text-center no-border"
                                        >
                                          {(
                                            Math.abs(row.v.max) *
                                            getScaleFactor({
                                              db: localisation,
                                              tag: "crack_width_",
                                              settings: props.user.settings,
                                            })
                                          ).toFixed(4)}
                                          {row.v.max === 0
                                            ? ""
                                            : row.v.max < 0
                                              ? " (" + getLabel(localisation, lang, "top_", true) + ")"
                                              : " (" + getLabel(localisation, lang, "bottom_", true) + ")"}
                                        </Col>
                                      )}
                                      {vXS !== 12 && (
                                        <Col
                                          id={`tab${2}-row${n}-col${2}`}
                                          style={{ flexBasis: `${((12 - uXS) * 100) / 12}%` }}
                                          className="text-center no-border"
                                        >
                                          {(
                                            row.v.min *
                                            getScaleFactor({
                                              db: localisation,
                                              tag: "crack_width_",
                                              settings: props.user.settings,
                                            })
                                          ).toFixed(4)}
                                          {row.v.min === 0
                                            ? ""
                                            : row.v.min < 0
                                              ? " (" + getLabel(localisation, lang, "top_", true) + ")"
                                              : " (" + getLabel(localisation, lang, "bottom_", true) + ")"}
                                        </Col>
                                      )}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          });
                        })}
                    </tbody>
                  </table>
                </Container>
              </Row>
            </Tab>

            {/** Design Results */}
            <Tab eventKey="3" title={getLabel(localisation, lang, "design_result_", true)}>
              <Row className="d-flex align-items-center justify-content-end" style={{ marginBottom: 5 }}>
                <Col xs={8}></Col>
                <Col xs={2} className={`text-center copy-message ${showCopyMessage ? "show" : ""}`}>
                  <span>{getLabel(localisation, lang, "table_copied_")}</span>
                </Col>
                <Col xs={2}>
                  {!isFirefox && (
                    <SXButton
                      button
                      size="sm"
                      className="modal-btn"
                      style={{ float: "right" }}
                      title="Copy table"
                      icon={faCopy}
                      onClick={() => copyToClipboard("copy-result-table-3")}
                    >
                      {getLabel(localisation, lang, "copy_project_")}
                    </SXButton>
                  )}
                </Col>
              </Row>
              <Row>
                <Container id="ngtable" className="copy-result-table-3">
                  <table className="table">
                    {/* HEADING */}
                    <thead>
                      <tr className="table-th-header align-items-center">
                        <th className="text-center table-header-bg">
                          {" "}
                          {getLabel(localisation, lang, "control_type_steel_") ?? ""}
                        </th>
                        <th className="text-center table-header-bg">
                          {" "}
                          {getLabel(localisation, lang, "utilisation_")}{" "}
                          {props.units.getDisplayHeader("utilisation_") ?? ""}
                        </th>
                        <th className="text-center table-header-bg">
                          {" "}
                          {getLabel(localisation, lang, "position_")} {props.units.getDisplayHeader("length_") ?? ""}
                        </th>
                        <th className="text-center table-header-bg"> {getLabel(localisation, lang, "loadcomb_")}</th>
                      </tr>
                    </thead>

                    {/* TABLE */}
                    <tbody>
                      {props.designResult[selLCO] &&
                        selLCO !== "" &&
                        Object.keys(props.designResult[selLCO]).map((key, index) => {
                          return props.designResult[selLCO][key].map((row, n) => {
                            return (
                              <tr className="table-body justify-content-center align-items-center ngtable" key={n}>
                                <td id={`tab${3}-row${n}-col${0}`} className="text-center no-border">
                                  {row.check}
                                </td>
                                <td
                                  id={`tab${3}-row${n}-col${1}`}
                                  className="text-center no-border"
                                  style={{ color: row.utilisation > 1.0 ? "red" : "green" }}
                                >
                                  {(
                                    row.utilisation *
                                    getScaleFactor({
                                      db: localisation,
                                      tag: "utilisation_",
                                      settings: props.user.settings,
                                    })
                                  ).toFixed(1)}
                                </td>
                                <td id={`tab${3}-row${n}-col${2}`} className="text-center no-border">
                                  {(
                                    row.pos *
                                    getScaleFactor({ db: localisation, tag: "length_", settings: props.user.settings })
                                  ).toFixed(3)}
                                </td>
                                <td id={`tab${3}-row${n}-col${3}`} className="text-center no-border">
                                  {row.lco}
                                </td>
                              </tr>
                            );
                          });
                        })}
                    </tbody>
                  </table>
                </Container>
              </Row>
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </>
  );
};
